/**
 * CustomerChatWidget.jsx · v2.7.45+E15 (Phase 5 · Feb-2026)
 * ─────────────────────────────────────────────────────────────────────
 * Customer-facing floating chat bubble. Drops into any customer page
 * (e.g. /portal, /tracker) to give riders a direct line to Buller Taxi
 * staff. Detects the customer's preferred language on first open and
 * stores the session_id + customer_token in localStorage so the same
 * conversation survives a page reload.
 *
 * The customer NEVER sees the staff-side English translation when the
 * customer is on Spanish — the widget only ever renders text in the
 * customer's language. The bilingual rendering lives in the staff
 * console.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Languages, Loader2, Check } from "lucide-react";

const STORAGE_KEY = "rtg_customer_chat_v1";
const POLL_MS = 5000;

const T = {
  en: {
    title: "Live chat",
    subtitle: "We typically reply within a few minutes",
    placeholder: "Type a message…",
    send: "Send",
    welcome: "Hi! How can we help?",
    namePrompt: "Your name (optional)",
    langPrompt: "Language",
    start: "Start chat",
    minimize: "Minimize",
    closed: "This conversation is closed.",
  },
  es: {
    title: "Chat en vivo",
    subtitle: "Normalmente respondemos en pocos minutos",
    placeholder: "Escribe un mensaje…",
    send: "Enviar",
    welcome: "¡Hola! ¿En qué podemos ayudarte?",
    namePrompt: "Tu nombre (opcional)",
    langPrompt: "Idioma",
    start: "Iniciar chat",
    minimize: "Minimizar",
    closed: "Esta conversación está cerrada.",
  },
};

function readStore() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
}
function writeStore(payload) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch { /* noop */ }
}

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function CustomerChatWidget() {
  const [open, setOpen] = useState(false);
  const [stored, setStored] = useState(() => readStore());
  const [lang, setLang] = useState(() => (readStore()?.lang) || (navigator.language?.startsWith("es") ? "es" : "en"));
  const [name, setName] = useState(() => readStore()?.name || "");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [sessionStatus, setSessionStatus] = useState("active");
  const endRef = useRef(null);

  const t = T[lang] || T.en;

  const fetchMessages = useCallback(async () => {
    if (!stored?.session_id || !stored?.token) return;
    try {
      const r = await fetch(`${API_BASE}/chat/sessions/${stored.session_id}/messages?token=${encodeURIComponent(stored.token)}`);
      if (!r.ok) return;
      const data = await r.json();
      setMessages(data.items || []);
      setSessionStatus(data.status || "active");
    } catch { /* silent */ }
  }, [stored]);

  // Poll for new messages while open
  useEffect(() => {
    if (!open || !stored?.session_id) return;
    fetchMessages();
    const t = setInterval(fetchMessages, POLL_MS);
    return () => clearInterval(t);
  }, [open, stored, fetchMessages]);

  // Auto-scroll
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const startChat = async () => {
    setStarting(true);
    try {
      const r = await fetch(`${API_BASE}/chat/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customer_name: name || (lang === "es" ? "Invitado" : "Guest"),
          customer_lang: lang,
          initial_message: draft || null,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const payload = { session_id: data.session_id, token: data.customer_token, lang, name };
      writeStore(payload);
      setStored(payload);
      setDraft("");
      setTimeout(fetchMessages, 200);
    } catch (e) { /* surface via state */ }
    finally { setStarting(false); }
  };

  const send = async () => {
    if (!draft.trim() || !stored?.session_id) return;
    setSending(true);
    const text = draft.trim();
    setDraft("");
    try {
      await fetch(`${API_BASE}/chat/sessions/${stored.session_id}/messages?token=${encodeURIComponent(stored.token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, lang }),
      });
      // Optimistic append
      setMessages((cur) => [...cur, {
        id: `local-${Date.now()}`,
        direction: "customer",
        sender_name: name || (lang === "es" ? "Tú" : "You"),
        text,
        lang,
        created_at: new Date().toISOString(),
      }]);
      setTimeout(fetchMessages, 800);
    } catch (e) { /* silent */ }
    finally { setSending(false); }
  };

  // Floating launcher button
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110"
        aria-label={t.title}
        data-testid="customer-chat-launcher"
      >
        <MessageCircle className="h-6 w-6"/>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-40 w-[360px] max-w-[90vw] h-[520px] bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded-lg shadow-2xl flex flex-col overflow-hidden" data-testid="customer-chat-widget">
      {/* Header */}
      <div className="bg-emerald-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="font-bold text-sm flex items-center gap-1.5"><MessageCircle className="h-4 w-4"/>{t.title}</div>
          <div className="text-[10px] text-emerald-50 truncate">{t.subtitle}</div>
        </div>
        <button type="button" onClick={() => setOpen(false)} className="hover:bg-white/20 rounded-full p-1" aria-label={t.minimize} data-testid="customer-chat-close">
          <X className="h-4 w-4"/>
        </button>
      </div>

      {/* Pre-start form */}
      {!stored && (
        <div className="flex-1 p-4 space-y-3">
          <div className="text-sm text-slate-700 dark:text-zinc-300">{t.welcome}</div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400 flex items-center gap-1">
              <Languages className="h-3 w-3"/>{t.langPrompt}
            </label>
            <div className="flex gap-1.5 mt-1">
              {["en", "es"].map((code) => (
                <button key={code} type="button" onClick={() => setLang(code)}
                  className={`flex-1 px-3 py-1.5 rounded text-xs font-bold transition-colors
                    ${lang === code ? "bg-emerald-600 text-white" : "bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 hover:bg-slate-200 dark:hover:bg-zinc-700"}`}
                  data-testid={`customer-chat-lang-${code}`}>
                  {code === "en" ? "English" : "Español"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-600 dark:text-zinc-400">{t.namePrompt}</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-1.5 mt-1 border border-slate-300 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900"
              data-testid="customer-chat-name-input"/>
          </div>
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
            placeholder={t.placeholder}
            className="w-full px-3 py-1.5 border border-slate-300 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900 resize-none"
            data-testid="customer-chat-first-msg"/>
          <button type="button" onClick={startChat} disabled={starting}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2 rounded text-sm flex items-center justify-center gap-1.5"
            data-testid="customer-chat-start">
            {starting ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
            {t.start}
          </button>
        </div>
      )}

      {/* Conversation */}
      {stored && (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50/50 dark:bg-zinc-900/30">
            {messages.length === 0 && (
              <div className="text-center text-xs text-slate-500 italic py-4">{t.welcome}</div>
            )}
            {messages.map((m) => {
              const mine = m.direction === "customer";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`customer-chat-msg-${m.id}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words
                    ${mine
                      ? "bg-emerald-600 text-white"
                      : "bg-white dark:bg-zinc-800 text-slate-900 dark:text-zinc-100 border border-slate-200 dark:border-zinc-700"}`}>
                    {!mine && <div className="text-[9px] uppercase tracking-widest font-bold mb-0.5 text-emerald-700 dark:text-emerald-400">{m.sender_name}</div>}
                    {m.text}
                  </div>
                </div>
              );
            })}
            <div ref={endRef}/>
          </div>
          {sessionStatus === "active" ? (
            <div className="border-t border-slate-200 dark:border-zinc-800 p-2 flex items-end gap-1.5">
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                rows={1} placeholder={t.placeholder}
                className="flex-1 px-2 py-1.5 border border-slate-300 dark:border-zinc-700 rounded text-sm bg-white dark:bg-zinc-900 resize-none"
                data-testid="customer-chat-input"/>
              <button type="button" onClick={send} disabled={sending || !draft.trim()}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white h-9 w-9 rounded flex items-center justify-center"
                aria-label={t.send} data-testid="customer-chat-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin"/> : <Send className="h-4 w-4"/>}
              </button>
            </div>
          ) : (
            <div className="border-t border-slate-200 dark:border-zinc-800 p-3 text-center text-xs italic text-slate-500 flex items-center justify-center gap-1.5">
              <Check className="h-3 w-3 text-emerald-600"/>{t.closed}
            </div>
          )}
        </>
      )}
    </div>
  );
}
